/* eslint-disable camelcase */
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { Feature as olFeature, Map as olMap, Overlay } from 'ol';
import { TileWMS } from 'ol/source';
import TileLayer from 'ol/layer/Tile';
import Feature from 'ol/format/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import buffer from '@turf/buffer';
import booleanIntersects from '@turf/boolean-intersects';
import difference from '@turf/difference';
import intersect from '@turf/intersect';
import union from '@turf/union';
import { featureCollection } from '@turf/helpers';
import type {
  Feature as GeoJSONFeature,
  Geometry as GeoJSONGeometry,
  Polygon as GeoJSONPolygon,
  MultiPolygon as GeoJSONMultiPolygon,
} from 'geojson';
import Geometry from 'ol/geom/Geometry';
import { IContext, IDrawing, IProvider, ISubTitlesLayer, IntersectLayerSelection, Tool } from './@types/Map';
import { ILayers } from './@types/Layers';
import { api } from 'services/api/api';
import { useFilter } from 'hooks/useFilter';
import Draw, { createBox, Options as DrawOptions } from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Circle, LineString, MultiPolygon, Point, Polygon } from 'ol/geom';
import { getArea, getLength } from 'ol/sphere.js';
import { unByKey } from 'ol/Observable.js';
import { Coordinate } from 'ol/coordinate';
import { EventsKey } from 'ol/events';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { createEmpty as createEmptyExtent, extend as extendExtent, intersects as intersectsExtent } from 'ol/extent';
import arrow from '../assets/img/arrow.png';
import whiteMarker from '../assets/img/white-marker.png';
import Interaction from 'ol/interaction/Interaction';
import { getUid } from 'ol/util';
import html2canvas from 'html2canvas';
import { Modal } from 'components/Modal';
import { Button } from 'components/Button';
import { useTranslateFeatures } from 'hooks/useTranslateFeatures';
import { useSwipe } from 'hooks/useSwipe';
import { useSplitFeatures } from 'hooks/useSplitFeatures';
import { useMergeFeatures } from 'hooks/useMergeFeatures';
import { useDissolveLayer } from 'hooks/useDissolveLayer';

export const MapContext = createContext<IContext>({} as IContext);

type PreparedFeature = {
  feature: olFeature<Geometry>;
  geometry: Geometry;
  extent: ReturnType<Geometry['getExtent']>;
  turfFeature: GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon> | null;
};

const getDrawStyle = (color: string, radius = 5) =>
  new Style({
    stroke: new Stroke({ color, width: 2 }),
    fill: new Fill({ color: `${color}33` }),
    image: new CircleStyle({
      radius: radius,
      fill: new Fill({ color }),
      stroke: new Stroke({ color, width: 1 }),
    }),
  });

const getArrowStyle = function (feature: any, color: string) {
  return (feature: any) => {
    const geometry = feature.getGeometry();
    const coordinates = geometry.getCoordinates();
    const length = coordinates.length;

    const end = coordinates[length - 1];
    const start = coordinates[length - 2];

    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const rotation = Math.atan2(dy, dx);

    return [
      // Linestring
      new Style({
        stroke: new Stroke({
          color: color,
          width: 2
        })
      }),
      // Arrow
      new Style({
        geometry: new Point(end),
        image: new Icon({
          color: color,
          src: arrow,
          anchor: [0.75, 0.5],
          rotateWithView: true,
          rotation: -rotation
        })
      })
    ];
  }
}

const getMarkerStyle = (color: string) =>
  new Style({
    stroke: new Stroke({ color, width: 2 }),
    fill: new Fill({ color: `${color}33` }),
    image: new Icon({
      color: color,
      src: whiteMarker,
      scale: 0.5,
      anchor: [0.5, 1],
      anchorXUnits: 'fraction',
      anchorYUnits: 'fraction',
    })
  })

interface IAction {
  draw: IDrawing[];
  action: 'erase' | 'draw' | 'edit';
}

export function MapProvider({ children }: IProvider) {
  const [map, setMap] = useState<olMap>();
  const mapRef = useRef<HTMLDivElement>(null);
  const [subTitles, setSubTitles] = useState<ISubTitlesLayer[]>([]);
  const [failedLoadTile, setFailedLoadTile] = useState<boolean>(false);
  const [layersFeatures, setLayersFeatures] = useState<Map<string, Feature[]>>();
  const [layerActiveFeature, setLayerActiveFeature] = useState<string | null>(null);
  const [openLayerFeaturesModal, setOpenLayerFeaturesModal] = useState<boolean>(false);
  const [openLayerDetailsModal, setOpenLayerDetailsModal] = useState<boolean>(false);
  const { filterData, mountObjectFilter } = useFilter();
  // O modal não é movido apenas quando definido como true, mas sim em toda alteração deste estado
  const [moveLayerFearuresModal, setMoveLayerFearuresModal] = useState<boolean>(false);
  const [dashboardOptions, setDashboardOptions] = useState(false);

  const drawLayerRef = useRef<VectorLayer<olFeature<Geometry>>>();

  const [drawings, setDrawings] = useState<IDrawing[]>([]);
  const drawingsRef = useRef<IDrawing[]>([]);
  const [addedLayers, setAddedLayers] = useState<ILayers[]>([]);
  const addedLayersRef = useRef<ILayers[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>('');

  useEffect(() => {
    drawingsRef.current = drawings;
  }, [drawings])

  useEffect(() => {
    addedLayersRef.current = addedLayers;
  }, [addedLayers]);


  const [undo, setUndo] = useState<IAction[]>([]);
  const undoRef = useRef<IAction[]>([]);
  useEffect(() => {
    undoRef.current = undo;
  }, [undo])

  const [overlays, setOverlays] = useState<{ [id: string]: Overlay }>({});
  const overlaysRef = useRef<{ [id: string]: Overlay }>({});
  useEffect(() => {
    overlaysRef.current = overlays;
  }, [overlays])

  const [actions, setActions] = useState<IAction[]>([]);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  const [drawMethods, setDrawMethods] = useState<{ [name: string]: Interaction }>({})
  const drawSource: VectorSource = new VectorSource();
  const { translateLayer, handleSetTranslateLayer } = useTranslateFeatures(map, selectedTool);
  const { swipeLayer, handleSetSwipeLayer, swipeDirection, setSwipeDirection } = useSwipe(map, selectedTool);
  const { splitLayer, handleSetSplitLayer } = useSplitFeatures(map, selectedTool);
  const {
    mergeLayer,
    handleSetMergeLayer,
    selectedFeatures,
    mergeError,
    isMerging,
    mergeSelected,
  } = useMergeFeatures(map, selectedTool);
  const [intersectFirstLayer, setIntersectFirstLayer] = useState<IntersectLayerSelection | null>(null);
  const [intersectSecondLayer, setIntersectSecondLayer] = useState<IntersectLayerSelection | null>(null);
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
  const [clipInputLayer, setClipInputLayer] = useState<VectorLayer<any> | null>(null);
  const [clipMaskLayer, setClipMaskLayer] = useState<VectorLayer<any> | null>(null);
  const [isClipping, setIsClipping] = useState<boolean>(false);
  const [bufferDistance, setBufferDistance] = useState<number>(0);
  const bufferDistanceRef = useRef<number>(0);
  const toolState = useRef<Tool | null>(null);

  useEffect(() => {
    bufferDistanceRef.current = bufferDistance;
  }, [bufferDistance]);

  const colorDrawRef = useRef("#006aff");
  const [colorDrawState, setColorDrawState] = useState("#006aff");

  const handleChangeColor = (color: string) => {
    colorDrawRef.current = color;
    setColorDrawState(color);
  };

  const showAlert = useCallback((message: string) => {
    setAlertMessage(message);
  }, []);

  const removeFileExtension = (name: string) => name.replace(/\.[^/.]+$/, '');

  const getUniqueTemporaryLayerTitle = (baseTitle: string) => {
    const normalizedBase = removeFileExtension(baseTitle.trim());
    const existingTitles = new Set(addedLayersRef.current.map(layer => layer.layertitle));

    if (!existingTitles.has(normalizedBase)) return normalizedBase;

    let count = 1;
    while (existingTitles.has(`${normalizedBase} (${count})`)) {
      count += 1;
    }
    return `${normalizedBase} (${count})`;
  };

  const createTextElement = (): HTMLElement => {
    const overlayElement = document.createElement('div');
    overlayElement.className = 'ol-tooltip ol-tooltip-measure';

    return overlayElement;
  }

  const createInputlement = (): HTMLElement => {
    const inputEl = document.createElement('input');
    inputEl.addEventListener('mousedown', e => e.stopPropagation());
    inputEl.addEventListener('pointerdown', e => e.stopPropagation());
    inputEl.addEventListener('click', e => e.stopPropagation());
    inputEl.placeholder = 'Insira seu texto';
    inputEl.className = 'autosize-input';
    inputEl.maxLength = 100;

    const container = document.createElement('div');
    container.className = 'label-overlay';
    container.appendChild(inputEl);

    return container;
  }

  const createTooltip = (feature: olFeature<Geometry>, offset: [number, number], element: HTMLElement, position?: Coordinate) => {
    const overlay = new Overlay({
      element: element,
      offset,
      positioning: 'bottom-center',
      stopEvent: false,
      insertFirst: false,
    });
    if (position) overlay.setPosition(position);

    map?.addOverlay(overlay);

    setOverlays(ov => ({ ...ov, [getUid(feature) as string]: overlay }));
  }

  const updateTooltip = (geometry: Geometry, uid: string) => {
    if (!Object.keys(overlaysRef.current).includes(uid)) return;
    const overlay = overlaysRef.current[uid];

    const el = overlay.getElement();
    switch (geometry.getType()) {
      case 'Circle':
        overlay.setPosition((geometry as Circle).getCenter());
        if (el) el.innerHTML = getCircleArea(geometry as Circle);
        break;
      case 'LineString':
        overlay.setPosition((geometry as LineString).getLastCoordinate());
        if (el) el.innerHTML = getLineLength((geometry as LineString));
        break;
      case 'Polygon':
        overlay.setPosition((geometry as Polygon).getInteriorPoint().getCoordinates());
        if (el) el.innerHTML = getPolygonArea((geometry as Polygon));
        break;
      case 'Point':
        overlay.setPosition((geometry as Point).getCoordinates());
        break;
      default:
        return;
    }
  }

  let listener: EventsKey | undefined;
  const listenToFeature = (feature: olFeature<Geometry>) => {

    listener = feature.getGeometry()?.on('change', function (evt: any) {
      if (!getUid(feature)) return;
      if (!Object.keys(overlaysRef.current).includes(`${getUid(feature)}`)) return;

      updateTooltip(evt.target, getUid(feature));
    });

  }

  const stopListening = () => {
    if (listener) unByKey(listener);
  }

  const getLineLength = function (line: LineString): string {
    const length = getLength(line);

    if (length > 100) return Math.round((length / 1000) * 100) / 100 + ' ' + 'km';

    return Math.round(length * 100) / 100 + ' ' + 'm';
  };

  const getPolygonArea = function (polygon: Polygon): string {
    const area = getArea(polygon);

    if (area > 10000) return Math.round((area / 1000000) * 100) / 100 + ' ' + 'km<sup>2</sup>';

    return Math.round(area * 100) / 100 + ' ' + 'm<sup>2</sup>';
  };

  const getCircleArea = function (circle: Circle) {
    const area = Math.PI * Math.pow(circle.getRadius(), 2);

    if (area > 10000) return Math.round((area / 1000000) * 100) / 100 + ' ' + 'km<sup>2</sup>';

    return Math.round(area * 100) / 100 + ' ' + 'm<sup>2</sup>';
  };

  useEffect(() => {
    if (!map) return;
    Object.keys(drawMethods).forEach(key => {
      drawMethods[key].setActive(false);
      map.addInteraction(drawMethods[key]);

      switch (key) {
        case 'edit':
          drawMethods[key].addEventListener('modifystart', (evt: any) => {
            listenToFeature(evt.features.getArray()[0]);
            saveAction(evt.features.getArray()[0], 'edit');
          })

          drawMethods[key].addEventListener('modifyend', () => {
            stopListening();
          })
          break;

        case 'marker':
          drawMethods[key].addEventListener('drawend', (evt: any) => {
            evt.feature.setStyle(getMarkerStyle(colorDrawRef.current));
            saveAction(evt.feature, 'draw');
          });
          break;


        case 'arrow':
          drawMethods[key].addEventListener('drawend', (evt: any) => {
            evt.feature.setStyle(getArrowStyle(evt.feature, colorDrawRef.current));
            saveAction(evt.feature, 'draw');
          });
          break;


        case 'text':
          drawMethods[key].addEventListener('drawend', (evt: any) => {
            evt.feature.setStyle(getDrawStyle(colorDrawRef.current, 7));
            createTooltip(evt.feature, [0, -10], createInputlement(), evt.feature.getGeometry().getCoordinates());
            saveAction(evt.feature, 'draw');
          });
          break;

        default:
          drawMethods[key].addEventListener('drawstart', (evt: any) => {
            evt.feature.setStyle(getDrawStyle(colorDrawRef.current));

            createTooltip(evt.feature, [0, -7], createTextElement());
            listenToFeature(evt.feature);
          })


          drawMethods[key].addEventListener('drawend', (evt: any) => {
            evt.feature.setStyle(getDrawStyle(colorDrawRef.current));
            stopListening();
            saveAction(evt.feature, 'draw');
          });
          break;
      }
    })
  }, [drawMethods])

  const createDraw = (type: any, options: Partial<DrawOptions> = {}) =>
    new Draw({
      source: drawSource,
      type,
      style: () => getDrawStyle(colorDrawRef.current),
      ...options,
    });

  const handleUndoGeomEdit = (act: IAction) => {
    const geom = act.draw[0].feature.getGeometry();
    if (!geom) return;
    const oldGeom = geom.clone().transform('EPSG:3857', 'EPSG:4326');
    act.draw[0].feature.setGeometry(act.draw[0].geom.transform('EPSG:4326', 'EPSG:3857'))
    act.draw[0].geom = oldGeom;

    updateTooltip(act.draw[0].feature.getGeometry(), getUid(act.draw[0].feature));
  }

  const resetUndo = () => {
    if (undoRef.current.length !== 0) {
      undoRef.current.forEach(action => {
        action.draw.forEach(d => {
          const el = overlaysRef.current[getUid(d.feature)].getElement();
          if (!el) return;
          if (Object.keys(overlaysRef.current).includes(getUid(d.feature))
            && el.style.display === 'none') {
            map?.removeOverlay(overlaysRef.current[getUid(d.feature)]);
            overlaysRef.current[getUid(d.feature)].dispose();
          };
        })
      })

      setOverlays(prev =>
        Object.fromEntries(
          Object.entries(prev).filter(([, overlay]) => !!overlay.getMap())
        )
      );
    }
    setUndo([]);
  }

  const handleUndo = () => {
    if (actions.length === 0) return;
    const act = actions.at(-1);
    if (!act) return;

    if (act.action === 'draw') { // apagar
      drawLayerRef.current?.getSource()?.removeFeature(drawings.at(-1)?.feature)
      const draw = drawings.at(-1);
      if (!draw) return;
      const el = overlays[draw.id].getElement();
      if (!el) return;

      if (Object.keys(overlays).includes(draw.id)) el.style.display = 'none'
      setDrawings(d => d.slice(0, -1));
    }
    else if (act.action === 'erase') { // desenhar
      act.draw.forEach(drawing => {
        setDrawings(d => [...d, drawing]);
        drawLayerRef.current?.getSource()?.addFeature(drawing.feature)
        const el = overlays[drawing.id].getElement();
        if (!el) return;
        if (Object.keys(overlays).includes(drawing.id)) el.style.display = ''
      })
    }
    else if (act.action === 'edit') {
      handleUndoGeomEdit(act);
    }

    setUndo(u => [...u, act]);
    setActions(ac => ac.slice(0, -1));
  }

  const handleRedo = () => {
    if (undo.length === 0) return;
    const act = undo.at(-1);
    if (!act) return;

    if (act.action === 'draw') { // desenhar
      act.draw.forEach(drawing => {
        setDrawings(d => [...d, drawing]);
        drawLayerRef.current?.getSource()?.addFeature(drawing.feature);
        const el = overlays[drawing.id].getElement();
        if (!el) return;
        if (Object.keys(overlays).includes(drawing.id)) el.style.display = ''
      })
    }
    else if (act.action === 'erase') { //apagar
      drawLayerRef.current?.getSource()?.removeFeature(drawings.at(-1)?.feature)
      setDrawings(d => d.slice(0, -1));

      const drawing = drawings.at(-1);
      if (!drawing) return;

      const el = overlays[drawing.id].getElement();
      if (!el) return;

      if (Object.keys(overlays).includes(drawing.id)) el.style.display = 'none'
    }
    else if (act.action === 'edit') {
      handleUndoGeomEdit(act);
    }

    setUndo(u => u.slice(0, -1));
    setActions(ac => [...ac, act]);
  }

  const handleChangeTool = (tool: Tool) => {
    setSelectedTool(t => (t?.action === tool.action ? null : tool));
  };

  // Apagar todos os desenhos
  const handleEraserAll = () => {
    drawLayerRef.current?.getSource()?.clear();
    Object.keys(overlaysRef.current).forEach(key => {
      const el = overlaysRef.current[key].getElement();
      if (el) el.style.display = 'none';
    })

    setDrawings([]);
    setActions(ac => [...ac, { draw: drawings, action: 'erase' } as IAction].slice(-20))
    resetUndo();
  }

  useEffect(() => {
    if (!map) return;

    if (selectedTool && Object.keys(drawMethods).includes(selectedTool.action)) {
      drawMethods[selectedTool.action].setActive(true);
    }

    if (toolState.current && Object.keys(drawMethods).includes(toolState.current.action)) {
      drawMethods[toolState.current.action].setActive(false);
    }

    // O estado de variáveis dentro de map.on não muda, é preciso o useRef
    toolState.current = selectedTool;
  }, [selectedTool]);

  function saveAction(feature: olFeature<Geometry>, action: string) {

    const geom = feature.getGeometry();
    if (!geom) return;

    const draw = {
      id: `${getUid(feature)}`,
      geom: geom.clone().transform('EPSG:3857', 'EPSG:4326'),
      feature: feature,
    }
    setDrawings(d => [
      ...d,
      draw,
    ]);

    setActions(ac => [...ac, { draw: [draw], action: action } as IAction].slice(-20));
    resetUndo();
  }

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!map) return;
    handleAddDrawLayer();
  }, [map]);

  const handleChangeOpacityLayer = (uuid: string, opacity: number) => {
    if (!map) return;

    const layers = map.getLayers().getArray();

    const layer = layers.find(item => item.getProperties().uuid === uuid);
    if (layer) {
      layer.setOpacity(opacity);
    }
  };

    
  //Visibilidade de camadas vetorias
  const handleChangeVisibility = (uuid: string, visibility: boolean) => {
    if (!map) return;

    const layers = map.getLayers().getArray();

    const layer = layers.find(item => item.getProperties().uuid === uuid);
    if (layer) {
      layer.setVisible(visibility);
    }
  };

  const getOpacityLayer = (uuid: string) => {
    if (!map) return -1;

    const layers = map.getLayers().getArray();

    const layer = layers.find(item => item.getProperties().uuid === uuid);
    if (layer) {
      return layer.getOpacity();
    }
    return -1;
  };

  const handleRemoveOverlayLayer = useCallback((uuid: string) => {
    if (!map) return;
    const layers = map.getLayers().getArray();

    const layerToDelete = layers.find(layer => layer.getProperties().uuid === uuid);
    if (layerToDelete) {
      map.removeLayer(layerToDelete);
    }
    // REMOVE DO MENU
    setAddedLayers(prev =>
      prev.filter(l => (l.layertitle + (l.layer_filter_id || '') + l.layername) !== uuid)
    );
  }, [map]);

  const handleApplyLayerFilter = (layerUuid: string, query: string | null, baseQuery?: string | null) => {
    if (!map) return;

    const mapLayer = map.getAllLayers().find(l => l.getProperties().uuid === layerUuid);
    const source: any = mapLayer && (mapLayer as any).getSource?.();
    const finalFilter = baseQuery && query
      ? `(${baseQuery}) AND (${query})`
      : (query || baseQuery || '');

    if (source && typeof source.updateParams === 'function') {
      source.updateParams({ CQL_FILTER: finalFilter });
      if (typeof source.refresh === 'function') {
        source.refresh();
      }
    }
  };

  const handleFitExtent = useCallback(async () => {
    if (!map) return;
    const mountFilter = mountObjectFilter();
    const response = await api.get('/pmextent', {
      params: {
        ...mountFilter,
      },
      paramsSerializer: {
        indexes: null,
      },
    });
    const extent = response.data[0].geoext;
    if (extent.length === 4) {
      map.getView().fit(extent, { duration: 2000 });
    }
  }, [map, filterData]);

  const resolveIntersectSelection = useCallback((id: string): IntersectLayerSelection | null => {
    if (!map || id === 'disable') return null;

    const [kind, rawId] = id.split('::');
    if (!kind || !rawId) return null;

    if (kind === 'vector') {
      const layer = map
        .getLayers()
        .getArray()
        .find((l: any) => {
          const props = l?.getProperties?.() ?? {};
          return l instanceof VectorLayer && (props.layertitle === rawId || props.uuid === rawId);
        }) as VectorLayer<any> | undefined;

      if (!layer) return null;

      const layertitle = String(layer.get('layertitle') || layer.get('uuid') || rawId);
      return {
        type: 'vector',
        id: rawId,
        layertitle,
        layer,
      };
    }

    if (kind === 'wms') {
      const layer = map
        .getLayers()
        .getArray()
        .find((l: any) => {
          const props = l?.getProperties?.() ?? {};
          const source = l?.getSource?.();
          return l instanceof TileLayer && source instanceof TileWMS && (props.uuid === rawId || props.layertitle === rawId);
        }) as TileLayer<any> | undefined;

      if (!layer) return null;

      const source = layer.getSource() as TileWMS;
      const params = source.getParams() || {};
      const layername = String(params.LAYERS || '');
      const host_url = String((source.getUrls() || [])[0] || '');
      if (!layername || !host_url) return null;

      return {
        type: 'wms',
        id: rawId,
        layertitle: String(layer.get('layertitle') || rawId),
        layername,
        host_url,
        layer_query_filter: params.CQL_FILTER ? String(params.CQL_FILTER) : '',
      };
    }

    return null;
  }, [map]);

  const handleSetIntersectFirstLayer = useCallback((id: string) => {
    setIntersectFirstLayer(resolveIntersectSelection(id));
  }, [resolveIntersectSelection]);

  const handleSetIntersectSecondLayer = useCallback((id: string) => {
    setIntersectSecondLayer(resolveIntersectSelection(id));
  }, [resolveIntersectSelection]);

  const handleSetClipInputLayer = useCallback((id: string) => {
    if (!map || id === 'disable') {
      setClipInputLayer(null);
      return;
    }

    const layer = map
      .getLayers()
      .getArray()
      .find((l: any) => {
        const props = l?.getProperties?.() ?? {};
        return (props.layertitle === id || props.uuid === id) && l instanceof VectorLayer;
      }) as VectorLayer<any> | undefined;

    if (layer) setClipInputLayer(layer);
  }, [map]);

  const handleSetClipMaskLayer = useCallback((id: string) => {
    if (!map || id === 'disable') {
      setClipMaskLayer(null);
      return;
    }

    const layer = map
      .getLayers()
      .getArray()
      .find((l: any) => {
        const props = l?.getProperties?.() ?? {};
        return (props.layertitle === id || props.uuid === id) && l instanceof VectorLayer;
      }) as VectorLayer<any> | undefined;

    if (layer) setClipMaskLayer(layer);
  }, [map]);

  const getExtentCenter = (extent: number[]) => {
    const [minX, minY, maxX, maxY] = extent;
    return [(minX + maxX) / 2, (minY + maxY) / 2] as Coordinate;
  };

  const getGeometrySampleCoords = (geometry: Geometry): Coordinate[] => {
    const type = geometry.getType();

    if (type === 'Point') {
      return [(geometry as Point).getCoordinates()];
    }

    if (type === 'LineString') {
      const coords = (geometry as LineString).getCoordinates();
      if (coords.length <= 2) return coords;
      return [coords[0], coords[Math.floor(coords.length / 2)], coords[coords.length - 1]];
    }

    if (type === 'Polygon') {
      const rings = (geometry as Polygon).getCoordinates();
      const outerRing = rings[0] || [];
      if (outerRing.length <= 4) return outerRing;
      return [
        outerRing[0],
        outerRing[Math.floor(outerRing.length / 3)],
        outerRing[Math.floor((outerRing.length * 2) / 3)],
      ];
    }

    const geomAny = geometry as any;
    if (geomAny?.getCoordinates) {
      const coords = geomAny.getCoordinates();
      if (Array.isArray(coords)) {
        const flattened: Coordinate[] = [];
        const walk = (value: any) => {
          if (!Array.isArray(value)) return;
          if (value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
            flattened.push(value as Coordinate);
            return;
          }
          value.forEach(walk);
        };
        walk(coords);
        if (flattened.length > 0) return [flattened[0], flattened[Math.floor(flattened.length / 2)], flattened[flattened.length - 1]];
      }
    }

    return [getExtentCenter(geometry.getExtent())];
  };

  const geometriesLikelyIntersect = (a: Geometry, b: Geometry) => {
    if (!a.intersectsExtent(b.getExtent()) || !b.intersectsExtent(a.getExtent())) {
      return false;
    }

    const aSamples = getGeometrySampleCoords(a);
    const bSamples = getGeometrySampleCoords(b);

    const aHitsB = aSamples.some(coord => {
      try {
        return (b as any).intersectsCoordinate?.(coord);
      } catch (e) {
        return false;
      }
    });

    if (aHitsB) return true;

    const bHitsA = bSamples.some(coord => {
      try {
        return (a as any).intersectsCoordinate?.(coord);
      } catch (e) {
        return false;
      }
    });

    if (bHitsA) return true;

    return true;
  };

  const buildPolygonDifferenceWithCandidates = (
    baseGeometry: Geometry,
    knifeFeatures: GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon>[]
  ): Geometry | null => {
    let current = geometryToTurfFeature(baseGeometry);
    if (!current) return null;
    if (knifeFeatures.length === 0) return baseGeometry.clone();
    const currentFeature = current;

    const relevantKnives = knifeFeatures.filter((knifeFeature) =>
      booleanIntersects(currentFeature, knifeFeature)
    );

    if (relevantKnives.length === 0) return baseGeometry.clone();

    const mergedKnife =
      relevantKnives.length >= 8
        ? union(featureCollection(relevantKnives)) || null
        : null;

    const knivesToApply = (mergedKnife ? [mergedKnife] : relevantKnives).filter(
      (
        knifeFeature
      ): knifeFeature is GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon> => !!knifeFeature
    );

    for (const knifeFeature of knivesToApply) {
      if (!booleanIntersects(current, knifeFeature)) continue;

      const clipped = difference(featureCollection([current, knifeFeature]));
      if (!clipped) return null;

      current = clipped;
    }

    return turfFeatureToOlGeometry(current);
  };

  const getFeaturesExtent = (features: olFeature<Geometry>[]): number[] | null => {
    if (!features || features.length === 0) return null;
    const extent = createEmptyExtent();
    let hasExtent = false;
    features.forEach((feature) => {
      const geom = feature.getGeometry();
      if (!geom) return;
      const geomExtent = geom.getExtent();
      if (!geomExtent || geomExtent.some((value) => !Number.isFinite(value))) return;
      extendExtent(extent, geomExtent);
      hasExtent = true;
    });
    return hasExtent ? extent : null;
  };

  const isLikelyGeographicExtent = (extent: number[] | null) => {
    if (!extent) return false;
    const [minX, minY, maxX, maxY] = extent;
    const maxAbsX = Math.max(Math.abs(minX), Math.abs(maxX));
    const maxAbsY = Math.max(Math.abs(minY), Math.abs(maxY));
    return maxAbsX <= 200 && maxAbsY <= 120;
  };

  const hasPolygonGeometries = (features: olFeature<Geometry>[]) =>
    features.some((feature) => {
      const geom = feature.getGeometry();
      return geom instanceof Polygon || geom instanceof MultiPolygon;
    });

  const geoJsonFormat = new GeoJSON();

  const validateIntersectLayersSelection = ({
    map,
    intersectFirstLayer,
    intersectSecondLayer,
  }: {
    map: any;
    intersectFirstLayer: any;
    intersectSecondLayer: any;
  }): string | null => {
    if (!map || !intersectFirstLayer || !intersectSecondLayer) {
      return 'Selecione duas camadas para aplicar a interseção.';
    }

    if (
      intersectFirstLayer.id === intersectSecondLayer.id &&
      intersectFirstLayer.type === intersectSecondLayer.type
    ) {
      return 'Selecione duas camadas diferentes.';
    }

    if (intersectFirstLayer.type === 'wms' && intersectSecondLayer.type === 'wms') {
      return 'A interseção não permite duas camadas do sistema. Selecione ao menos uma camada via arquivo.';
    }

    return null;
  };

  const getVectorLayerFeatures = (layerRef: any): olFeature<Geometry>[] => {
    const source = layerRef.layer.getSource() as VectorSource | null;
    if (!source) {
      throw new Error('Não foi possível acessar as feições da camada selecionada.');
    }

    return source.getFeatures() as olFeature<Geometry>[];
  };

  const validateWmsIntersectionSupport = (otherLayerRef: any): void => {
    const otherSource = otherLayerRef?.layer?.getSource?.() as VectorSource | null;
    const extent = otherSource?.getExtent?.();

    if (!otherSource || !extent || extent.length !== 4) {
      throw new Error(
        'Para usar camada do sistema, a outra camada deve ser via arquivo e possuir feições válidas.'
      );
    }
  };

  const loadWmsLayerFeatures = async (
    layerRef: any,
    otherLayerRef: any,
    buildWfsUrl: (
      hostUrl: string,
      layerName: string,
      filter?: string,
      bbox?: number[]
    ) => string
  ): Promise<olFeature<Geometry>[]> => {

    validateWmsIntersectionSupport(otherLayerRef);

    try {

      let bbox: number[] | undefined;

      if (otherLayerRef.type === 'vector') {

        const otherSource =
          otherLayerRef.layer.getSource?.() as VectorSource | null;

        const extent = otherSource?.getExtent?.();

        if (
          extent &&
          extent.length === 4 &&
          extent.every(Number.isFinite)
        ) {
          bbox = extent;
        }
      }

      const geojson = new GeoJSON();

      const url = buildWfsUrl(
        layerRef.host_url,
        layerRef.layername,
        layerRef.layer_query_filter,
        bbox
      );

      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors'
      });

      const json = await response.json();

      return geojson.readFeatures(json, {
        dataProjection: 'EPSG:3857',
        featureProjection: 'EPSG:3857',
      }) as olFeature<Geometry>[];

    } catch {

      throw new Error(
        'Não foi possível buscar as feições da camada do sistema no geoserver.'
      );

    }
  };

  const loadLayerFeatures = async (
    layerRef: any,
    otherLayerRef: any,
    buildWfsUrl: (hostUrl: string, layerName: string, filter?: string) => string
  ): Promise<olFeature<Geometry>[]> => {
    if (layerRef.type === 'vector') {
      return getVectorLayerFeatures(layerRef);
    }

    return loadWmsLayerFeatures(layerRef, otherLayerRef, buildWfsUrl);
  };

  const geometryToTurfFeature = (
    geometry: Geometry
  ): GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon> | null => {
    const genericFeature = geometryToGeoJSONFeature(geometry);
    if (!genericFeature) return null;

    if (
      genericFeature.geometry.type !== 'Polygon' &&
      genericFeature.geometry.type !== 'MultiPolygon'
    ) {
      return null;
    }

    return genericFeature as GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon>;
  };

  const geometryToGeoJSONFeature = (
    geometry: Geometry,
    {
      dataProjection = 'EPSG:3857',
      featureProjection = 'EPSG:3857',
    }: {
      dataProjection?: string;
      featureProjection?: string;
    } = {}
  ): GeoJSONFeature<GeoJSONGeometry> | null => {
    const geometryObject = geoJsonFormat.writeGeometryObject(geometry, {
      dataProjection,
      featureProjection,
    }) as GeoJSONGeometry | null;

    if (!geometryObject) return null;

    return {
      type: 'Feature',
      properties: {},
      geometry: geometryObject,
    };
  };

  const turfFeatureToOlGeometry = (
    feature: GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon>,
    {
      dataProjection = 'EPSG:3857',
      featureProjection = 'EPSG:3857',
    }: {
      dataProjection?: string;
      featureProjection?: string;
    } = {}
  ): Geometry | null => {
    const geometry = geoJsonFormat.readGeometry(feature.geometry, {
      dataProjection,
      featureProjection,
    });

    if (geometry instanceof Polygon || geometry instanceof MultiPolygon) {
      return geometry;
    }

    return null;
  };

  const prepareFeaturesForIntersection = (
    features: olFeature<Geometry>[]
  ): PreparedFeature[] => {
    return features
      .map((feature) => {
        const geometry = feature.getGeometry();
        if (!geometry) return null;

        return {
          feature,
          geometry,
          extent: geometry.getExtent(),
          turfFeature: geometryToTurfFeature(geometry),
        };
      })
      .filter((item): item is PreparedFeature => !!item);
  };

  const intersectPreparedFeatureCollections = (
    firstFeatures: olFeature<Geometry>[],
    secondFeatures: olFeature<Geometry>[]
  ): olFeature<Geometry>[] => {
    const preparedFirst = prepareFeaturesForIntersection(firstFeatures);
    const preparedSecond = prepareFeaturesForIntersection(secondFeatures);

    const resultFeatures: olFeature<Geometry>[] = [];
    const addedIds = new Set<string>();

    preparedFirst.forEach((firstItem, indexFirst) => {
      if (!firstItem.turfFeature) return;

      preparedSecond.forEach((secondItem, indexSecond) => {
        const firstTurf = firstItem.turfFeature;
        const secondTurf = secondItem.turfFeature;

        if (!firstTurf || !secondTurf) return;

        const intersection = intersect(
          featureCollection([firstTurf, secondTurf])
        );

        if (!intersection) return;

        const olGeometry = turfFeatureToOlGeometry(
          intersection as GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon>
        );

        if (!olGeometry) return;

        const clone = firstItem.feature.clone();
        clone.setGeometry(olGeometry);
        clone.setId(firstItem.feature.getId() || `intersect_${indexFirst}_${indexSecond}_0`);

        const cloneId = String(
          clone.getId() || `intersect_${indexFirst}_${indexSecond}_0`
        );

        if (addedIds.has(cloneId)) return;

        addedIds.add(cloneId);
        resultFeatures.push(clone as olFeature<Geometry>);
      });
    });

    return resultFeatures;
  };

  const buildIntersectionDiagnostics = (
    firstFeatures: olFeature<Geometry>[],
    secondFeatures: olFeature<Geometry>[]
  ): string[] => {
    const messages: string[] = [];

    const firstExtent = getFeaturesExtent(firstFeatures);
    const secondExtent = getFeaturesExtent(secondFeatures);
    const extentsOverlap =
      firstExtent && secondExtent ? intersectsExtent(firstExtent, secondExtent) : true;

    const projectionMismatchHint =
      firstExtent &&
      secondExtent &&
      isLikelyGeographicExtent(firstExtent) !== isLikelyGeographicExtent(secondExtent);

    const firstHasPolygon = hasPolygonGeometries(firstFeatures);
    const secondHasPolygon = hasPolygonGeometries(secondFeatures);

    if (!extentsOverlap) {
      messages.push('Não há sobreposição espacial entre as camadas selecionadas.');
    } else {
      messages.push('Não foi possível gerar interseção entre as camadas selecionadas.');
    }

    if (!firstHasPolygon || !secondHasPolygon) {
      messages.push('A interseção atual suporta apenas geometrias poligonais.');
    }

    if (projectionMismatchHint) {
      messages.push('Possível incompatibilidade de projeção');
    }

    return messages;
  };

  const buildIntersectionLayer = ({
    resultFeatures,
    firstTitle,
    secondTitle,
    getUniqueTemporaryLayerTitle,
  }: {
    resultFeatures: olFeature<Geometry>[];
    firstTitle: string;
    secondTitle: string;
    getUniqueTemporaryLayerTitle: (baseName: string) => string;
  }): ILayers => {
    const source = new VectorSource({ features: resultFeatures });

    const timestamp = Date.now();
    const layerTitle = getUniqueTemporaryLayerTitle(
      `intersecao_${removeFileExtension(firstTitle)}_x_${removeFileExtension(secondTitle)}`
    );

    return {
      id: timestamp,
      area_tematica_id: 'upload',
      sub_tematica_id: null,
      order: 0,
      metadados_camadas_id: null,
      layer_order_id: 'upload',
      layer_order: { order: 0 } as any,
      area_tematica: 'Upload',
      layertitle: layerTitle,
      link_shapefile: '',
      layername: layerTitle,
      host_url: '',
      metadado_url: '',
      layertype: 'VECTOR',
      sourcetype: 'VECTOR',
      visible: true,
      metadados: {
        camada: '',
        categoria: '',
        dado_primario: true,
        data: '',
        fonte: '',
        id: 0,
        organizacao: '',
        representacao: '',
        resumo: '',
        subtema: '',
        tema: '',
        tipo_geom: '',
        tipo_insercao: '',
      },
      nova_camada: true,
      layer_filter_id: 0,
      layer_query_filter: '',
      atributos: [],
      is_vector: true,
      vector_source: source,
    };
  };

  const fitLayerExtentOnMap = (map: any, source: VectorSource) => {
    const extent = source.getExtent();

    if (extent && extent.length === 4) {
      map.getView().fit(extent, {
        padding: [40, 40, 40, 40],
        duration: 500,
      });
    }
  };

  const createBufferGeometry = (geometry: Geometry, meters: number): Geometry | null => {
    if (!Number.isFinite(meters) || meters < 0) return null;
    if (meters === 0) return geometry.clone();

    const turfFeature = geometryToGeoJSONFeature(geometry, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });

    if (!turfFeature) return null;

    const bufferedFeature = buffer(turfFeature, meters, {
      units: 'meters',
      steps: 24,
    });

    if (!bufferedFeature) return null;

    return turfFeatureToOlGeometry(bufferedFeature, {
      dataProjection: 'EPSG:4326',
      featureProjection: 'EPSG:3857',
    });
  };

  const applyBufferToGeometry = (geometry: Geometry, style: any) => {
    if (!map) return;
    const distance = bufferDistanceRef.current;
    if (!Number.isFinite(distance) || distance < 0) {
      showAlert('Informe uma distância de buffer em metros maior ou igual a zero.');
      return;
    }

    const bufferGeometry = createBufferGeometry(geometry, distance);
    if (!bufferGeometry) {
      showAlert('Não foi possível gerar o buffer para a feição selecionada.');
      return;
    }

    const feature = new olFeature({ geometry: bufferGeometry });
    feature.setStyle(style);
    const source = new VectorSource({ features: [feature] });
    const timestamp = Date.now();
    const layerTitle = getUniqueTemporaryLayerTitle(`buffer_${distance}m`);

    const resultLayer: ILayers = {
      id: timestamp,
      area_tematica_id: 'upload',
      sub_tematica_id: null,
      order: 0,
      metadados_camadas_id: null,
      layer_order_id: 'upload',
      layer_order: { order: 0 } as any,
      area_tematica: 'Upload',
      layertitle: layerTitle,
      link_shapefile: '',
      layername: layerTitle,
      host_url: '',
      metadado_url: '',
      layertype: 'VECTOR',
      sourcetype: 'VECTOR',
      visible: true,
      metadados: {
        camada: '',
        categoria: '',
        dado_primario: true,
        data: '',
        fonte: '',
        id: 0,
        organizacao: '',
        representacao: '',
        resumo: '',
        subtema: '',
        tema: '',
        tipo_geom: '',
        tipo_insercao: '',
      },
      nova_camada: true,
      layer_filter_id: 0,
      layer_query_filter: '',
      atributos: [],
      is_vector: true,
      vector_source: source,
    };

    handleAddOverlayLayer(resultLayer);

    const extent = source.getExtent();
    if (extent && extent.length === 4) {
      map.getView().fit(extent, { padding: [40, 40, 40, 40], duration: 500 });
    }
  };

  const handleAddDrawLayer = function () {
    if (!map) return;

    const drawLayer = new VectorLayer({
      source: drawSource,
      zIndex: 999,
    });
    drawLayerRef.current = drawLayer;

    setDrawMethods({
      ruler: createDraw('LineString'),
      square: createDraw('Circle', { geometryFunction: createBox() }),
      circle: createDraw('Circle'),
      pencil: createDraw('Polygon'),
      dot: createDraw('Point'),
      marker: createDraw('Point', { style: () => getMarkerStyle(colorDrawRef.current) }),
      arrow: createDraw('LineString', { maxPoints: 2 }),
      text: createDraw('Point'),
      edit: new Modify({ source: drawSource }),
    })

    map.addLayer(drawLayer);

    map.on('pointermove', function (evt) {
      if (toolState.current?.action !== 'edit' && toolState.current?.action !== 'eraser') {
        map.getTargetElement().style.cursor = '';
        return;
      }

      let cursor = '';

      map.forEachFeatureAtPixel(
        evt.pixel,
        function () {
          return (cursor = 'pointer');
        },
        {
          hitTolerance: 10,
        },
      );

      map.getTargetElement().style.cursor = cursor;
    });

    map.on('click', evt => {
      if (toolState.current?.action === 'eraser') {
        const source = drawLayer.getSource();

        const features: olFeature<Geometry>[] = [];
        map.forEachFeatureAtPixel(
          evt.pixel,
          function (feature) {
            if (!(feature instanceof olFeature)) return;
            features.push(feature as olFeature);
          },
          {
            hitTolerance: 10,
          },
        );

        features.sort((a, b) => +getUid(b) - +getUid(a));

        if (features[0]) {
          const feature: olFeature<Geometry> = features[0];

          const drawing = drawingsRef.current.find(d => d.id === getUid(feature));
          if (!drawing) return;

          setActions(ac => [...ac, { draw: [drawing], action: 'erase' }])
          resetUndo();

          setDrawings(draw => draw.filter(d => d.id !== getUid(feature)));

          if (Object.keys(overlaysRef.current).includes(getUid(feature))) {
            const el = overlaysRef.current[getUid(feature)].getElement();
            if (el) el.style.display = 'none'
          }

          source?.removeFeature(feature);
        }
      }
    });

    map.on('singleclick', async evt => {
      if (toolState.current?.action !== 'buffer') return;

      let geometryToBuffer: Geometry | null = null;
      let style;
      map.forEachFeatureAtPixel(
        evt.pixel,
        (feature, layer) => {
          if (!layer) return false;
          if (layer === drawLayer) return false;
          if (!layer.getVisible()) return false;
          const geom = (feature as olFeature<Geometry>)?.getGeometry?.();
          const olFeatureInstance = feature as olFeature<Geometry>;
          style = olFeatureInstance.getStyle()
          if (!geom) return false;

          geometryToBuffer = geom.clone();
          return true;
        },
        { hitTolerance: 8 }
      );

      if (geometryToBuffer) {
        applyBufferToGeometry(geometryToBuffer, style);
        return;
      }

      const mapLayers = map.getLayers().getArray();
      const geojsonFormat = new GeoJSON();

      for (let i = mapLayers.length - 1; i >= 0; i -= 1) {
        const currentLayer = mapLayers[i];
        if (!currentLayer.getVisible()) continue;
        if (!(currentLayer instanceof TileLayer)) continue;
        const source = currentLayer.getSource();
        if (!(source instanceof TileWMS)) continue;

        const url = source.getFeatureInfoUrl(
          evt.coordinate,
          map.getView().getResolution() || 100,
          'EPSG:3857',
          { FEATURE_COUNT: 1, INFO_FORMAT: 'application/json' }
        );

        if (!url) continue;

        try {
          const resp = await fetch(url, { method: 'GET', mode: 'cors' });
          const json = await resp.json();
          const features = geojsonFormat.readFeatures(json, {
            dataProjection: 'EPSG:3857',
            featureProjection: 'EPSG:3857',
          });
          if (!features || features.length === 0) continue;

          const geom = features[0].getGeometry();
          style = features[0].getStyle();
          if (!geom) continue;
          
          applyBufferToGeometry(geom.clone(), style);
          return;
        } catch (error) {
          // tenta próxima camada
          continue;
        }
      }

      showAlert('Nenhuma feição encontrada no ponto clicado para aplicar buffer.');
    });
  };

  const handleAddOverlayLayer = useCallback(
    (layer: ILayers) => {
      if (!map) return;
      const { layername, host_url, layertitle, layer_order, layer_filter_id, layer_query_filter } =
        layer;
      const uuid = layertitle + (layer_filter_id || '') + layername;
      const alreadyExists = map.getAllLayers().some(l => l.getProperties().uuid === uuid);
      if (alreadyExists) return;

      const zIndex = layer_order.order || 0;

      const wmsSource: TileWMS = new TileWMS({
        url: host_url,
        crossOrigin: 'Anonymous',
        params: {
          LAYERS: layername,
          TILED: true,
          VERSION: '1.3.0',
          FORMAT: 'image/png',
          SRS: 'EPSG:4674',
          CQL_FILTER: layer_query_filter && layer_query_filter,
        },
        serverType: 'geoserver',
        hidpi: false,
      });

      const overlay = new TileLayer({
        source: wmsSource,
        properties: {
          uuid,
        },
        zIndex,
        opacity: 1,
      });

      if (layer.is_vector && layer.vector_source) {
       
        const vectorLayer = new VectorLayer({
          source: layer.vector_source,
          zIndex,
          properties: {
            uuid,
            isVector: true,
            layertitle: layer.layertitle,
          },
        });

        map.addLayer(vectorLayer);

        setAddedLayers(prev => {
          const exists = prev.find(
            l =>
              l.layertitle === layer.layertitle &&
              l.layername === layer.layername
          );
          if (exists) return prev;
          return [...prev, layer];
        });

      }
      
      map.addLayer(overlay);

      setSubTitles(prevState => [
        ...prevState,
        {
          layername,
          url: !(layer.is_vector && layer.vector_source) ? wmsSource.getLegendUrl(map.getView().getResolution())! : "",
        },
      ]);

      // ==========================
      // CLICK TO VIEW ATTRIBUTES
      // ==========================
      map.on('singleclick', evt => {
        setLayersFeatures(new Map());
        if (!!toolState.current) return;

        const layers = map.getLayers().getArray();
        const layerSelected = layers.find(layer => layer.getProperties().uuid === uuid);
        if (!layerSelected || !layerSelected.getVisible()) return;
        setLayerActiveFeature(layerSelected?.getProperties().uuid);

        const featuresClicked: any[] = [];

        map.forEachFeatureAtPixel(
          evt.pixel,
          (feature, clickedLayer) => {
            if (!clickedLayer || !clickedLayer.get('isVector')) return false;

            const props = { ...feature.getProperties() };
            const geometry = props.geometry
              ? geoJsonFormat.writeGeometryObject(props.geometry, {
                  featureProjection: 'EPSG:3857',
                  dataProjection: 'EPSG:3857',
                })
              : undefined;

            featuresClicked.push({
              type: 'feature',
              id: `${clickedLayer.get('layertitle')}_${Date.now()}`,
              geometry,
              geometry_name: feature.get('geometryName_') ?? 'geometry',
              layername: clickedLayer.get('layertitle'),
              properties: props,
            });
            return false;
          },
          { hitTolerance: 5 }
        );

        const wmsPromise =
          layerSelected && !openLayerFeaturesModal
            ? (() => {
                const url = wmsSource.getFeatureInfoUrl(
                  evt.coordinate,
                  map.getView().getResolution() ?? 100,
                  'EPSG:3857',
                  { FEATURE_COUNT: 50, INFO_FORMAT: 'application/json' }
                );
                if (!url) return Promise.resolve([]);
                return fetch(url, { method: 'GET', mode: 'cors' })
                  .then(res => res.json())
                  .then(html => html.features ?? [])
                  .catch(() => []);
              })()
            : Promise.resolve([]);

        wmsPromise.then(wmsFeatures => {
          const hasVector = featuresClicked.length > 0;
          const hasWms = wmsFeatures.length > 0;

          if (!hasVector && !hasWms) return;

          setLayersFeatures(prev => {
            const updated = new Map(prev);
            if (hasVector) updated.set('user_layers', featuresClicked);
            if (hasWms) updated.set(`${layer.layername}&&${layer.id}&&${uuid}`, wmsFeatures);
            return updated;
          });

          setOpenLayerFeaturesModal(true);
          setMoveLayerFearuresModal(moveModal => !moveModal);
        });
      });

      map.on('pointermove', function movedrag(evt) {
        if (evt.dragging) {
          return;
        }
        const data = overlay.getData(evt.pixel);
        map.getTargetElement().style.cursor = data ? 'pointer' : '';
      });

      return () => {
        map.removeLayer(overlay);
      };
    },
    [map],
  );

  const {
    dissolveLayer,
    dissolveAttribute,
    handleSetDissolveLayer,
    handleSetDissolveAttribute,
    handleApplyDissolveLayer,
  } = useDissolveLayer({
    map,
    handleAddOverlayLayer,
    showAlert,
    getUniqueTemporaryLayerTitle,
  });

  const buildWfsUrl = (
    hostUrl: string,
    layername: string,
    layerQueryFilter?: string,
    bbox?: number[]
  ) => {
    const url = new URL(hostUrl);
    url.pathname = url.pathname.replace(/\/wms$/i, '/wfs');
    url.searchParams.set('service', 'WFS');
    url.searchParams.set('version', '1.0.0');
    url.searchParams.set('request', 'GetFeature');
    url.searchParams.set('typeName', layername);
    url.searchParams.set('outputFormat', 'application/json');
    url.searchParams.set('srsName', 'EPSG:3857');

    if (layerQueryFilter) {
      url.searchParams.set('CQL_FILTER', layerQueryFilter);
    }

    if (bbox) {

      // aplica um buffer no bbox, sem ele algumas features tavam ficando de fora
      const padding = 200;
      const paddedBbox = [
        bbox[0] - padding,
        bbox[1] - padding,
        bbox[2] + padding,
        bbox[3] + padding,
      ];
      url.searchParams.set(
        'bbox',
        `${paddedBbox.join(',')},EPSG:3857`
      );
    }

    return url.toString();
  };

  const handleApplyIntersectLayers = useCallback(async () => {
    if (isIntersecting) return;

    const validationError = validateIntersectLayersSelection({
      map,
      intersectFirstLayer,
      intersectSecondLayer,
    });

    if (validationError) {
      showAlert(validationError);
      return;
    }

    if (!map || !intersectFirstLayer || !intersectSecondLayer) {
      return;
    }

    const firstTitle = intersectFirstLayer.layertitle || 'camada_a';
    const secondTitle = intersectSecondLayer.layertitle || 'camada_b';

    setIsIntersecting(true);

    try {
      let firstFeatures: olFeature<Geometry>[] = [];
      let secondFeatures: olFeature<Geometry>[] = [];

      try {
        firstFeatures = await loadLayerFeatures(
          intersectFirstLayer,
          intersectSecondLayer,
          buildWfsUrl
        );

        secondFeatures = await loadLayerFeatures(
          intersectSecondLayer,
          intersectFirstLayer,
          buildWfsUrl
        );
      } catch (error) {
        showAlert(error instanceof Error ? error.message : 'Erro ao carregar feições das camadas.');
        return;
      }

      if (firstFeatures.length === 0 || secondFeatures.length === 0) {
        showAlert('As camadas selecionadas não possuem feições para interseção.');
        return;
      }

      const resultFeatures = intersectPreparedFeatureCollections(firstFeatures, secondFeatures);

      if (resultFeatures.length === 0) {
        const messages = buildIntersectionDiagnostics(firstFeatures, secondFeatures);
        showAlert(messages.join('\n'));
        return;
      }

      const resultLayer = buildIntersectionLayer({
        resultFeatures,
        firstTitle,
        secondTitle,
        getUniqueTemporaryLayerTitle,
      });

      handleAddOverlayLayer(resultLayer);

      if (resultLayer.vector_source) {
        fitLayerExtentOnMap(map, resultLayer.vector_source);
      }
    } finally {
      setIsIntersecting(false);
    }
  }, [
    isIntersecting,
    map,
    intersectFirstLayer,
    intersectSecondLayer,
    handleAddOverlayLayer,
    showAlert,
    getUniqueTemporaryLayerTitle,
  ]);


  const handleApplyClipLayers = useCallback(() => {
    if (isClipping) return;

    if (!map || !clipInputLayer || !clipMaskLayer) {
      showAlert('Selecione a camada de entrada e a camada limite para aplicar o clip.');
      return;
    }

    const inputId = clipInputLayer.get('uuid') || clipInputLayer.get('layertitle');
    const maskId = clipMaskLayer.get('uuid') || clipMaskLayer.get('layertitle');

    if (inputId === maskId) {
      showAlert('Selecione duas camadas diferentes.');
      return;
    }

    const inputSource = clipInputLayer.getSource() as VectorSource | null;
    const maskSource = clipMaskLayer.getSource() as VectorSource | null;

    if (!inputSource || !maskSource) {
      showAlert('Não foi possível acessar as fontes vetoriais das camadas.');
      return;
    }

    const inputFeatures = inputSource.getFeatures();
    const maskFeatures = maskSource.getFeatures();

    if (inputFeatures.length === 0 || maskFeatures.length === 0) {
      showAlert('As camadas selecionadas não possuem feições para o clip.');
      return;
    }

    setIsClipping(true);

    setTimeout(() => {
      void (async () => {
        try {
          const clippedFeatures: olFeature<Geometry>[] = [];
          const addedIds = new Set<string>();
          const maskItems = maskFeatures
            .map((maskFeature) => {
              const maskGeometry = maskFeature.getGeometry();
              if (!maskGeometry) return null;

              return {
                extent: maskGeometry.getExtent(),
                geometry: maskGeometry,
                turfFeature: geometryToTurfFeature(maskGeometry),
              };
            })
            .filter((item): item is {
              extent: ReturnType<Geometry['getExtent']>;
              geometry: Geometry;
              turfFeature: GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon> | null;
            } => !!item);

          const batchSize = 20;

          for (let index = 0; index < inputFeatures.length; index += 1) {
            if (index > 0 && index % batchSize === 0) {
              await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            }

            const inputFeature = inputFeatures[index];
            const inputGeometry = inputFeature.getGeometry();
            if (!inputGeometry) continue;

            const clone = inputFeature.clone();
            clone.setId(inputFeature.getId() || `clip_${index}`);

            const cloneId = String(clone.getId() || `clip_${index}`);
            if (addedIds.has(cloneId)) continue;

            const inputExtent = inputGeometry.getExtent();
            const candidateMasks = maskItems.filter((maskItem) =>
              intersectsExtent(inputExtent, maskItem.extent)
            );

            if (candidateMasks.length === 0) {
              addedIds.add(cloneId);
              clippedFeatures.push(clone as olFeature<Geometry>);
              continue;
            }

            const inputTurf = geometryToGeoJSONFeature(inputGeometry);
            const intersectingMasks = candidateMasks.filter((maskItem) => {
              if (inputTurf && maskItem.turfFeature) {
                return booleanIntersects(inputTurf, maskItem.turfFeature);
              }

              return geometriesLikelyIntersect(inputGeometry, maskItem.geometry);
            });

            if (intersectingMasks.length === 0) {
              addedIds.add(cloneId);
              clippedFeatures.push(clone as olFeature<Geometry>);
              continue;
            }

            if (inputGeometry instanceof Polygon || inputGeometry instanceof MultiPolygon) {
              const knifeFeatures = intersectingMasks
                .map((maskItem) => maskItem.turfFeature)
                .filter(
                  (
                    feature
                  ): feature is GeoJSONFeature<GeoJSONPolygon | GeoJSONMultiPolygon> => !!feature
                );

              const clippedGeometry = buildPolygonDifferenceWithCandidates(
                inputGeometry,
                knifeFeatures
              );

              if (!clippedGeometry) continue;

              clone.setGeometry(clippedGeometry);
              addedIds.add(cloneId);
              clippedFeatures.push(clone as olFeature<Geometry>);
              continue;
            }

            addedIds.add(cloneId);
            clippedFeatures.push(clone as olFeature<Geometry>);
          }

          if (clippedFeatures.length === 0) {
            showAlert('Todas as feições da camada de entrada foram removidas pelo clip.');
            return;
          }

          const source = new VectorSource({ features: clippedFeatures });
          const inputTitle = String(clipInputLayer.get('layertitle') || 'entrada');
          const maskTitle = String(clipMaskLayer.get('layertitle') || 'limite');
          const timestamp = Date.now();
          const layerTitle = getUniqueTemporaryLayerTitle(`clip_${removeFileExtension(inputTitle)}_menos_${removeFileExtension(maskTitle)}`);

          const resultLayer: ILayers = {
            id: timestamp,
            area_tematica_id: 'upload',
            sub_tematica_id: null,
            order: 0,
            metadados_camadas_id: null,
            layer_order_id: 'upload',
            layer_order: { order: 0 } as any,
            area_tematica: 'Upload',
            layertitle: layerTitle,
            link_shapefile: '',
            layername: layerTitle,
            host_url: '',
            metadado_url: '',
            layertype: 'VECTOR',
            sourcetype: 'VECTOR',
            visible: true,
            metadados: {} as any,
            nova_camada: true,
            layer_filter_id: 0,
            layer_query_filter: '',
            atributos: [],
            is_vector: true,
            vector_source: source,
          };

          handleAddOverlayLayer(resultLayer);

          const extent = source.getExtent();
          if (extent && extent.length === 4) {
            map.getView().fit(extent, {
              padding: [40, 40, 40, 40],
              duration: 500,
            });
          }
        } finally {
          setIsClipping(false);
        }
      })();
    }, 0);
  }, [map, clipInputLayer, clipMaskLayer, isClipping, handleAddOverlayLayer, showAlert, getUniqueTemporaryLayerTitle]);

  const handleAddAllOverlayLayers = useCallback((layers: ILayers[]) => {
    if (!map) return;
    layers.forEach(layer => handleAddOverlayLayer(layer));
  }, [handleAddOverlayLayer, map]);

  const handleRemoveOverlayLayers = useCallback((layers: ILayers[]) => {
    if (!map) return;
    layers.forEach(layer => handleRemoveOverlayLayer(layer.layertitle + (layer.layer_filter_id || '') + layer.layername));
  }, [handleRemoveOverlayLayer, map]);

  const handleRemoveAllOverlayLayers = useCallback((layers: ILayers[]) => {
    if (!map) return;
    layers.forEach(layer => handleRemoveOverlayLayer(layer.layertitle + (layer.layer_filter_id || '') + layer.layername));
    setOpenLayerFeaturesModal(false);
  }, [handleRemoveOverlayLayer, map]);

  const handleClearAllMapLayers = useCallback(() => {
    if (!map) return;
    const layers = map.getLayers().getArray().slice();
    layers.forEach(layer => {
      const isBaseMap = layer.get('isBaseMap') || (layer.getZIndex() === 0 && !layer.getProperties()?.uuid && !layer.get('isVector'));
      if (!isBaseMap) {
        map.removeLayer(layer);
      }
    });
    setAddedLayers([]);
    setSubTitles([]);
    setLayersFeatures(new Map());
    setLayerActiveFeature(null);
    setOpenLayerFeaturesModal(false);
    setOpenLayerDetailsModal(false);
    drawLayerRef.current?.getSource()?.clear();
    setDrawings([]);
    setSelectedTool(null);
  }, [map]);

  useEffect(() => {
    const handleClearDisplay = () => {
      handleClearAllMapLayers();
    };

    window.addEventListener('p3m:clearMapDisplay', handleClearDisplay);
    return () => {
      window.removeEventListener('p3m:clearMapDisplay', handleClearDisplay);
    };
  }, [handleClearAllMapLayers]);

  const handleDownloadPrintMap = async () => {
    if (!mapRef.current) return;

    const canvasScreenshot = await html2canvas(mapRef.current, {
      scale: 2,
      useCORS: true,
    });

    const dataURL = canvasScreenshot.toDataURL('image/jpeg', 1);
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = 'mapa.jpeg';
    link.click();
  };

  return (
    <MapContext.Provider
      value={{
        handleSetSwipeLayer,
        swipeLayer,
        swipeDirection,
        setSwipeDirection,
        map,
        mapRef,
        setMap,
        subTitles,
        dashboardOptions,
        failedLoadTile,
        setFailedLoadTile,
        layersFeatures,
        layerActiveFeature,
        openLayerFeaturesModal,
        setOpenLayerFeaturesModal,
        openLayerDetailsModal,
        setOpenLayerDetailsModal,
        moveLayerFearuresModal,
        setMoveLayerFearuresModal,
        setDashboardOptions,
        handleAddAllOverlayLayers,
        handleAddOverlayLayer,
        handleFitExtent,
        handleRemoveOverlayLayer,
        handleRemoveOverlayLayers,
        handleRemoveAllOverlayLayers,
        handleClearAllMapLayers,
        handleChangeOpacityLayer,
        handleChangeVisibility,
        getOpacityLayer,
        handleApplyLayerFilter,
        handleDownloadPrintMap,
        handleChangeTool,
        selectedTool,
        drawings,
        handleEraserAll,
        handleChangeColor,
        handleUndo,
        handleRedo,
        colorDraw: colorDrawState,
        amountActionsRedo: undo.length,
        amountActions: actions.length,
        addedLayers,
        setAddedLayers,
        handleSetTranslateLayer,
        translateLayer,
        splitLayer,
        handleSetSplitLayer,
        mergeLayer,
        handleSetMergeLayer,
        selectedFeatures,
        mergeError,
        isMerging,
        mergeSelected,
        intersectFirstLayer,
        intersectSecondLayer,
        isIntersecting,
        handleSetIntersectFirstLayer,
        handleSetIntersectSecondLayer,
        handleApplyIntersectLayers,
        clipInputLayer,
        clipMaskLayer,
        isClipping,
        handleSetClipInputLayer,
        handleSetClipMaskLayer,
        handleApplyClipLayers,
        dissolveLayer,
        dissolveAttribute,
        handleSetDissolveLayer,
        handleSetDissolveAttribute,
        handleApplyDissolveLayer,
        bufferDistance,
        setBufferDistance,
      }}
    >
      {children}
      {!!alertMessage && (
        <Modal onDisabled={() => setAlertMessage('')}>
          <div style={{ minWidth: 360, maxWidth: 520 }}>
            <h2 style={{ marginBottom: 12 }}>
              Aviso
            </h2>
            <p style={{ marginBottom: 20, whiteSpace: 'pre-line' }}>
              {alertMessage}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setAlertMessage('')}>
                OK
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </MapContext.Provider>
  );
}
